import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Caption from 'LuxKit/Typography/Caption'
import InlineIcon from 'components/utils/InlineIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import type { MouseEventHandler, ReactNode } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import FormatCurrency from 'components/Common/FormatCurrency'

const ClickableIcon = styled(Clickable)`
  // Vertically align the icon with text
  display: inline-flex;
  vertical-align: sub;
`

interface Props {
  /**
   * @option estimate - when we don't have enough data to calculate the exact points e.g. no dates entered
   * @option exact - when we have enough data to calculate the exact points the customer will earn
   * @option additional - 'earn an additional' amount of points
   * @option nominal - we want to display that the user will earn points without showing the numerical amount
   * @option ratio - we want to display the ratio of points earned per currency unit: e.g. 1.5 points per $1
   */
  calculationType: 'estimate' | 'exact' | 'additional' | 'nominal' | 'ratio'
  earnablePoints: number
  statusCredits?: number
  pointsEarnRatio?: number
  trailingCaption?: string
  /** does not propagate and is default-prevented */
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

function PriceRowLoyaltyPointsContent({
  calculationType,
  earnablePoints,
  statusCredits,
  pointsEarnRatio,
  trailingCaption,
  onInfoIconClick,
}: Props) {
  const intl = useIntl()

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onInfoIconClick?.(event)
  }, [onInfoIconClick])

  const showPoints = calculationType !== 'nominal'
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const hasStatusCredits = showPoints && !!statusCredits

  const pointsValue = <FormatLoyaltyPoints abbreviated points={earnablePoints} />
  const statusCreditsValue = <FormatLoyaltyStatusCredits statusCredits={statusCredits ?? 0} />

  const caption = trailingCaption
    ? <Caption variant="medium" colour="neutral-three" weight="normal" as="span">{trailingCaption}</Caption>
    : ''

  const infoIcon = onInfoIconClick
    ? <ClickableIcon onClick={handleClick}>
        <InlineIcon Icon={LineInfoCircleIcon} colour="neutral-one" />
    </ClickableIcon>
    : ''

  const messageValues = {
    points: pointsValue,
    statusCredits: statusCreditsValue,
    caption,
    infoIcon,
    programName: loyaltyProgramName,
    span: (children: ReactNode) => <span>{children}</span>,
    nowrap: (children: ReactNode) => {
      // Return null if the children is empty
      const isEmpty = Array.isArray(children)
        ? children.every(child => !child || (typeof child === 'string' && !child.trim()))
        : !children || (typeof children === 'string' && !children.trim())
      if (isEmpty) return null
      return <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
    },
  }

  const earningLabel = (() => {
    if (!showPoints) {
      return intl.formatMessage(
        {
          id: 'O2s2K8lSD1InkAFdje_4',
          defaultMessage: '<span>Earn {programName} Points</span><nowrap> {caption} {infoIcon}</nowrap>',
          description: 'Loyalty points text when showing nominal earn message without a numeric value.',
        },
        messageValues,
      )
    }

    switch (calculationType) {
      case 'estimate':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: '4DPxSkEzi9cRuSwnGyAP',
                defaultMessage: '<span>Earn from {points}</span><nowrap><span> + {statusCredits}</span> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing estimated minimum earnable points and status credits.',
              },
              messageValues,
            )
          : intl.formatMessage(
              {
                id: '1UpWR8ZDUw2NuNA8WoR_',
                defaultMessage: '<span>Earn from {points}</span><nowrap> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing estimated minimum earnable points.',
              },
              messageValues,
            )
      case 'exact':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'yGIaostTEo00DoA-YZf2',
                defaultMessage: '<span>Earn {points}</span><nowrap><span> + {statusCredits}</span> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing exact earnable points and status credits.',
              },
              messageValues,
            )
          : intl.formatMessage(
              {
                id: 'n4ZRiUDGafJ4qfb3h35N',
                defaultMessage: '<span>Earn {points}</span><nowrap> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing exact earnable points.',
              },
              messageValues,
            )
      case 'additional':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'bRMskeDO9KTbs5lDA5Lc',
                defaultMessage: '<span>Earn an additional {points}</span><nowrap><span> + {statusCredits}</span> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing additional earnable points and status credits.',
              },
              messageValues,
            )
          : intl.formatMessage(
              {
                id: 'pD1r5BzzLxkYC30yK8Xm',
                defaultMessage: '<span>Earn an additional {points}</span><nowrap> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text when showing additional earnable points.',
              },
              messageValues,
            )
      case 'ratio':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'KJvIkTOJd_XZb1HnWQTA',
                defaultMessage: '<span>Earn {points} per {price}</span><nowrap><span> + {statusCredits}</span> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text showing points earned per currency unit and status credits.',
              },
              {
                ...messageValues,
                points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />,
                price: <FormatCurrency value={1} />,
              },
            )
          : intl.formatMessage(
              {
                id: 'TttuOt8FVYvES-qZpd7Q',
                defaultMessage: '<span>Earn {points} per {price}</span><nowrap> {caption} {infoIcon}</nowrap>',
                description: 'Loyalty points text showing points earned per one unit of currency.',
              },
              {
                ...messageValues,
                points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />,
                price: <FormatCurrency value={1} />,
              },
            )
    }
  })()

  return <span>{earningLabel}</span>
}

export default PriceRowLoyaltyPointsContent
